/**
 * @fileoverview Collapsible sub-agent call renderer.
 * Shows live duration/token/update metrics in the collapsed row and keeps the
 * sub-agent content hidden until the user expands it.
 */

'use client';

import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, ChevronRight, Loader2, XCircle } from 'lucide-react';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Markdown } from '@/components/ui/markdown';
import { cn } from '@/lib/utils';
import type { AgentCallStreamStats } from '../../agent-call-stream-state';
import { AnimatedCounter } from '../../animated-counter';
import { estimateDisplayTokenCount } from '../../text-metrics';

const STREAM_TOKEN_METRIC_INTERVAL_MS = 600;

/**
 * Props for AgentCallCardRenderer
 */
interface AgentCallCardRendererProps {
  input: {
    agentId?: string;
    agentName?: string;
    avatar?: string;
    explanation?: string;
    instructions?: string;
    roleDescription?: string;
    status?: string;
  };
  output: Record<string, unknown> | string | null;
  isLoading: boolean;
  isIncomplete: boolean;
  /** Computed status label from parent (e.g. Running, Completed, Failed, Incomplete) */
  status?: string;
  /** Live streaming text from the sub-agent for real-time preview */
  streamingText?: string;
  /** Live display metrics captured from sub-agent stream events */
  streamStats?: AgentCallStreamStats;
  /**
   * Final streamed text captured at stream-end. Used after streaming completes
   * so the card shows the full sub-agent reply instead of the (truncated)
   * master-facing tool output. Session-only; reload falls back to `output`.
   */
  finalText?: string;
}

/**
 * Checks if the tool call output indicates success.
 */
function isToolCallSuccessful(output: Record<string, unknown> | string): boolean {
  if (typeof output === 'string') return !/^\s*(error|failed)[:\s]/i.test(output);
  if (typeof output.success === 'boolean') return output.success;
  if (output.error) return false;
  if (output.status === 'error') return false;
  return true;
}

/**
 * Extracts a text preview from tool output for the collapsible result section.
 */
function extractOutputPreview(output: Record<string, unknown> | string): string {
  if (typeof output === 'string') return output.slice(0, 500);

  const candidate = output.content ?? output.text ?? output.result ?? output.output;
  if (typeof candidate === 'string') return candidate.slice(0, 500);

  try {
    return JSON.stringify(output, null, 2).slice(0, 500);
  } catch {
    return 'Output available';
  }
}

/** Builds a compact one-line preview for the collapsed card. */
function buildCollapsedPreview(value?: string): string | undefined {
  const preview = value
    ?.replace(/```[\s\S]*?```/g, '')
    .replace(/[#*_`[\]()]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (!preview) return undefined;
  return preview.length > 140 ? `${preview.slice(0, 140)}...` : preview;
}

/** Returns true when a completed worker output only contains its own label. */
function isAgentLabelOnlyContent(value: string | undefined, agentName: string): boolean {
  if (!value) return false;
  const normalized = value.trim();
  return normalized === `[${agentName}]` || normalized === agentName;
}

/** Builds useful fallback content when the worker did not return visible text. */
function buildFallbackContent(input: AgentCallCardRendererProps['input']): string {
  const sections: string[] = [];

  if (input.explanation) {
    sections.push(`**Task**\n\n${input.explanation}`);
  }
  if (input.roleDescription) {
    sections.push(`**Role**\n\n${input.roleDescription}`);
  }
  if (input.instructions) {
    const criteria = input.instructions
      .split('\n')
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item) => `- ${item}`)
      .join('\n');
    if (criteria) {
      sections.push(`**Acceptance Criteria**\n\n${criteria}`);
    }
  }

  return sections.join('\n\n');
}

/** Returns elapsed seconds for display. */
function getElapsedSeconds(startedAt: number, endedAt: number | undefined, now: number): number {
  return Math.max(0, Math.round(((endedAt ?? now) - startedAt) / 1000));
}

/**
 * Limits how often a rapidly-streaming numeric metric updates on screen.
 *
 * The underlying stream can update for every token/chunk, but the collapsed
 * row only needs a few visible changes per second. Final values apply
 * immediately so completed cards never show stale metrics.
 */
function useThrottledMetricValue(
  value: number,
  isStreaming: boolean,
  intervalMs = STREAM_TOKEN_METRIC_INTERVAL_MS,
): number {
  const [displayValue, setDisplayValue] = useState(value);
  const lastUpdateRef = useRef(Date.now());
  const timeoutRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!isStreaming) {
      if (timeoutRef.current !== undefined) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
      }
      lastUpdateRef.current = Date.now();
      setDisplayValue(value);
      return;
    }

    const elapsedMs = Date.now() - lastUpdateRef.current;
    if (elapsedMs >= intervalMs) {
      lastUpdateRef.current = Date.now();
      setDisplayValue(value);
      return;
    }

    if (timeoutRef.current !== undefined) {
      window.clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(() => {
      lastUpdateRef.current = Date.now();
      setDisplayValue(value);
      timeoutRef.current = undefined;
    }, intervalMs - elapsedMs);

    return () => {
      if (timeoutRef.current !== undefined) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
      }
    };
  }, [intervalMs, isStreaming, value]);

  return displayValue;
}

/**
 * Renders a collapsed-by-default sub-agent call with live metrics.
 */
export function AgentCallCardRenderer({
  input,
  output,
  isLoading,
  isIncomplete,
  status: statusLabel,
  streamingText,
  streamStats,
  finalText,
}: AgentCallCardRendererProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [now, setNow] = useState(Date.now());
  const localStartedAtRef = useRef(Date.now());
  const streamEndRef = useRef<HTMLDivElement>(null);

  const agentName = input.agentName || 'AI Employee';
  const hasStreamingContent = isLoading && !!streamingText;
  const hasFinalText = !isLoading && !!finalText;
  const hasOutput = !isLoading && !!output;
  const isExpandable = hasStreamingContent || hasFinalText || hasOutput;
  const isError = isIncomplete || (!isLoading && output && !isToolCallSuccessful(output));
  const taskHint = input.status || input.explanation || input.roleDescription;
  const visibleContent =
    streamingText ||
    finalText ||
    (output ? extractOutputPreview(output) : '');
  const hasOnlyLabelContent = isAgentLabelOnlyContent(visibleContent, agentName);
  const fallbackContent = buildFallbackContent(input);
  const displayContent = hasOnlyLabelContent && fallbackContent ? fallbackContent : visibleContent;
  const previewText =
    buildCollapsedPreview(hasOnlyLabelContent ? fallbackContent : streamingText || finalText) ||
    taskHint;
  const hasDurationMetric = Boolean(streamStats) || isLoading;
  const startedAt = streamStats?.startedAt ?? localStartedAtRef.current;
  const elapsedSeconds = getElapsedSeconds(startedAt, streamStats?.endedAt, now);
  const tokenCount =
    streamStats?.estimatedTokens ?? estimateDisplayTokenCount(displayContent);
  const displayTokenCount = useThrottledMetricValue(tokenCount, isLoading);

  useEffect(() => {
    if (!isLoading || streamStats?.endedAt) return;
    const intervalId = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [isLoading, streamStats?.endedAt]);

  /**
   * Auto-scroll only when the user has explicitly opened the card.
   */
  useEffect(() => {
    if (isOpen && streamingText && streamEndRef.current) {
      streamEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [isOpen, streamingText]);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger
        className={cn(
          'group flex w-full max-w-3xl items-start gap-2 rounded-md px-2 py-1.5 -ml-2 text-left text-sm text-muted-foreground',
          'hover:bg-muted/50 hover:text-foreground transition-colors cursor-pointer'
        )}
        disabled={!isExpandable}
      >
        <span className="mt-0.5 flex shrink-0 items-center gap-1">
          {isLoading ? (
            <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
          ) : isError ? (
            <XCircle className="size-3.5 text-red-500" />
          ) : (
            <CheckCircle2 className="size-3.5 text-emerald-500" />
          )}
          <ChevronRight
            className={cn(
              'size-3.5 transition-transform duration-200',
              isOpen && 'rotate-90',
              !isExpandable && 'opacity-30'
            )}
          />
        </span>

        <span className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_auto] gap-x-2 gap-y-0.5 overflow-hidden">
          <span className="flex min-w-0 items-center gap-1.5 overflow-hidden">
            <span className="shrink-0 text-muted-foreground">
              {isLoading ? 'Calling' : isError ? 'Failed' : 'Called'}
            </span>
            <span className="min-w-0 truncate font-medium text-foreground">
              {agentName}
            </span>
            {statusLabel && (
              <span className={cn(
                'shrink-0 rounded-sm px-1.5 py-0.5 text-[10px] leading-none',
                isError
                  ? 'bg-red-500/10 text-red-600'
                  : 'bg-emerald-500/10 text-emerald-700'
              )}>
                {statusLabel}
              </span>
            )}
          </span>

          <span className="flex min-w-0 shrink-0 items-center justify-end gap-x-1.5 text-xs text-muted-foreground">
            {hasDurationMetric && (
              <>
                <span className="text-muted-foreground/60">·</span>
                <span className="inline-flex items-center gap-1 tabular-nums">
                  <AnimatedCounter value={elapsedSeconds} />
                  <span>s</span>
                </span>
                <span className="text-muted-foreground/60">|</span>
              </>
            )}
            <span className="inline-flex items-center gap-1 tabular-nums">
              <span>~</span>
              <AnimatedCounter value={displayTokenCount} />
              <span>output tokens</span>
            </span>
          </span>
          {previewText && (
            <span className="col-span-2 line-clamp-1 text-xs text-muted-foreground/75">
              {previewText}
            </span>
          )}
        </span>
      </CollapsibleTrigger>

      {isExpandable && (
        <CollapsibleContent>
          <div className="ml-6 mt-1 mb-1 max-h-72 max-w-3xl overflow-y-auto rounded-md border border-border/50 bg-muted/35 p-3 text-xs text-muted-foreground">
            <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border/50 pb-2 text-[11px]">
              <span>Status: {statusLabel || (isLoading ? 'Running' : isError ? 'Failed' : 'Completed')}</span>
              {hasDurationMetric && <span>Duration: {elapsedSeconds}s</span>}
              <span>Output tokens: ~{tokenCount.toLocaleString()}</span>
            </div>
            {hasStreamingContent ? (
              <div className="max-w-none text-xs">
                <Markdown className="prose-sm text-xs" density="chat">
                  {streamingText}
                </Markdown>
                <div ref={streamEndRef} />
              </div>
            ) : hasFinalText || (hasOnlyLabelContent && fallbackContent) ? (
              <div className="max-w-none text-xs">
                <Markdown className="prose-sm text-xs" density="chat">
                  {displayContent}
                </Markdown>
              </div>
            ) : (
              <pre className="font-mono whitespace-pre-wrap break-words">
                {extractOutputPreview(output as Record<string, unknown> | string)}
              </pre>
            )}
          </div>
        </CollapsibleContent>
      )}
    </Collapsible>
  );
}
